import React, { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { 
  Phone, 
  Mail, 
  MapPin, 
  Send,
  Briefcase,
  Users,
  MessageSquare,
  CheckCircle
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import PageHeader from '../components/layout/PageHeader';
import { contactApi } from '../lib/api';
import { toast } from 'sonner';
import { cn } from '../lib/utils';

const formTypes = [
  { 
    id: 'general', 
    label: 'General Inquiry', 
    icon: MessageSquare,
    description: 'Have a question? We are here to help.'
  },
  { 
    id: 'hire_talent', 
    label: 'Hire Talent', 
    icon: Users,
    description: 'Find top IT professionals for your team.'
  },
  { 
    id: 'submit_requirement', 
    label: 'Submit Requirement', 
    icon: Briefcase,
    description: 'Share your project requirements with us.'
  }
];

const contactInfo = [
  {
    icon: Mail,
    label: "Email",
    value: "info@gitallc.com",
    href: "mailto:info@gitallc.com"
  },
  {
    icon: Phone,
    label: "Phone",
    value: "718-554-3222",
    href: "tel:+1-718-554-3222"
  },
  {
    icon: MapPin,
    label: "Address",
    value: "1110 South Ave\nStaten Island, NY 10314",
    href: null
  }
];

export default function ContactPage() {
  const [searchParams] = useSearchParams();
  const initialType = searchParams.get('type') || 'general';
  
  const [activeTab, setActiveTab] = useState(initialType);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    company: '',
    subject: '',
    message: ''
  });

  useEffect(() => {
    const type = searchParams.get('type');
    if (type && ['general', 'hire_talent', 'submit_requirement', 'career_inquiry'].includes(type)) {
      setActiveTab(type === 'career_inquiry' ? 'general' : type);
    }
  }, [searchParams, setActiveTab]);

  const handleInputChange = useCallback((e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      await contactApi.submit({
        ...formData,
        form_type: activeTab
      });
      setSubmitted(true);
      toast.success('Your message has been sent! We will get back to you soon.');
      setFormData({ name: '', email: '', phone: '', company: '', subject: '', message: '' });
    } catch (err) {
      if (process.env.NODE_ENV === 'development') {
        console.error('Error submitting form:', err);
      }
      toast.error('Failed to send message. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="pt-20">
        <PageHeader 
          title="Contact Us"
          subtitle="We'd love to hear from you. Reach out and let's start a conversation."
          breadcrumbs={[{ label: "Contact" }]}
        />
        
        <section className="section-padding bg-white">
          <div className="container-padding">
            <div className="max-w-xl mx-auto text-center">
              <div className="w-20 h-20 rounded-full bg-green-100 flex items-center justify-center mx-auto mb-6">
                <CheckCircle className="w-10 h-10 text-green-600" />
              </div>
              <h2 
                className="text-3xl font-bold text-[#0A2540] mb-4"
                style={{ fontFamily: 'Outfit, sans-serif' }}
                data-testid="success-title"
              >
                Message Sent Successfully!
              </h2>
              <p className="text-lg text-slate-600 mb-8">
                Thank you for reaching out. Our team will review your message and get back to you within 24-48 hours.
              </p>
              <div className="flex flex-col sm:flex-row justify-center gap-4">
                <Button 
                  onClick={() => setSubmitted(false)}
                  className="btn-primary"
                  data-testid="send-another-btn"
                >
                  Send Another Message
                </Button>
                <Link to="/">
                  <Button variant="outline" className="btn-outline" data-testid="go-home-btn">
                    Back to Home
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  return (
    <div className="pt-20">
      <PageHeader 
        title="Contact Us"
        subtitle="We'd love to hear from you. Reach out and let's start a conversation."
        breadcrumbs={[{ label: "Contact" }]}
      />

      <section className="section-padding bg-white" data-testid="contact-section">
        <div className="container-padding">
          <div className="grid lg:grid-cols-3 gap-12">
            {/* Contact Info */}
            <div className="lg:col-span-1">
              <h2 
                className="text-2xl font-bold text-[#0A2540] mb-6"
                style={{ fontFamily: 'Outfit, sans-serif' }}
              >
                Get in Touch
              </h2>
              <p className="text-slate-600 mb-8">
                Whether you need top IT talent, want to discuss a project, or have questions about our services - we're here to help.
              </p>

              <div className="space-y-6">
                {contactInfo.map((info, index) => (
                  <div key={info.label} className="flex items-start" data-testid={`contact-info-${index}`}>
                    <div className="w-12 h-12 rounded-xl bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-[#2563EB]" />
                    </div>
                    <div className="ml-4">
                      <div className="text-sm font-medium text-slate-500">{info.label}</div>
                      {info.href ? (
                        <a 
                          href={info.href}
                          className="text-[#0A2540] hover:text-[#2563EB] transition-colors whitespace-pre-line"
                        >
                          {info.value}
                        </a>
                      ) : (
                        <div className="text-[#0A2540] whitespace-pre-line">{info.value}</div>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Office Hours */}
              <div className="mt-10 p-6 bg-slate-50 rounded-xl">
                <h3 className="font-semibold text-[#0A2540] mb-3">Office Hours</h3>
                <div className="space-y-2 text-sm text-slate-600">
                  <div className="flex justify-between">
                    <span>Monday - Friday</span>
                    <span>10:00 AM - 5:00 PM EST</span>
                  </div>
                  <div className="flex justify-between">
                    <span>Saturday - Sunday</span>
                    <span>Closed</span>
                  </div>
                </div>
              </div>
            </div>

            {/* Contact Form */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-2xl border border-slate-100 p-8">
                <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
                  <TabsList className="grid w-full grid-cols-3 mb-8" data-testid="form-type-tabs">
                    {formTypes.map((type) => (
                      <TabsTrigger 
                        key={type.id} 
                        value={type.id}
                        className="flex items-center gap-2 py-3"
                        data-testid={`tab-${type.id}`}
                      >
                        <type.icon className="w-4 h-4" />
                        <span className="hidden sm:inline">{type.label}</span>
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {formTypes.map((type) => (
                    <TabsContent key={type.id} value={type.id}>
                      <div className="mb-6">
                        <h3 className="text-xl font-semibold text-[#0A2540]">{type.label}</h3>
                        <p className="text-slate-600">{type.description}</p>
                      </div>

                      <form onSubmit={handleSubmit} className="space-y-6" data-testid={`contact-form-${type.id}`}>
                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="name">Full Name *</Label>
                            <Input 
                              id="name"
                              name="name"
                              value={formData.name}
                              onChange={handleInputChange}
                              placeholder="John Doe"
                              required
                              className="h-12"
                              data-testid="contact-name-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="email">Email *</Label>
                            <Input 
                              id="email"
                              name="email"
                              type="email"
                              value={formData.email}
                              onChange={handleInputChange}
                              placeholder="john@company.com"
                              required
                              className="h-12"
                              data-testid="contact-email-input"
                            />
                          </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-6">
                          <div className="space-y-2">
                            <Label htmlFor="phone">Phone</Label>
                            <Input 
                              id="phone"
                              name="phone"
                              type="tel"
                              value={formData.phone}
                              onChange={handleInputChange}
                              placeholder="+1 (555) 123-4567"
                              className="h-12"
                              data-testid="contact-phone-input"
                            />
                          </div>
                          <div className="space-y-2">
                            <Label htmlFor="company">Company</Label>
                            <Input 
                              id="company"
                              name="company"
                              value={formData.company}
                              onChange={handleInputChange}
                              placeholder="Your Company"
                              className="h-12"
                              data-testid="contact-company-input"
                            />
                          </div>
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="subject">Subject *</Label>
                          <Input 
                            id="subject"
                            name="subject"
                            value={formData.subject}
                            onChange={handleInputChange}
                            placeholder={
                              type.id === 'hire_talent' 
                                ? "e.g., Need 5 Java Developers for 6-month project" 
                                : type.id === 'submit_requirement'
                                ? "e.g., Cloud Migration Project - AWS"
                                : "How can we help?"
                            }
                            required
                            className="h-12"
                            data-testid="contact-subject-input"
                          />
                        </div>

                        <div className="space-y-2">
                          <Label htmlFor="message">Message *</Label>
                          <Textarea 
                            id="message"
                            name="message"
                            value={formData.message}
                            onChange={handleInputChange}
                            placeholder={
                              type.id === 'hire_talent'
                                ? "Please describe the roles you're looking to fill, required skills, experience levels, and timeline..."
                                : type.id === 'submit_requirement'
                                ? "Please describe your project requirements, technology stack, timeline, and any specific needs..."
                                : "Tell us more about your inquiry..."
                            }
                            required
                            rows={6}
                            data-testid="contact-message-input"
                          />
                        </div>

                        <Button 
                          type="submit" 
                          className="btn-primary w-full md:w-auto"
                          disabled={submitting}
                          data-testid="contact-submit-btn"
                        >
                          {submitting ? (
                            <>
                              <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                              Sending...
                            </>
                          ) : (
                            <>
                              <Send className="w-4 h-4 mr-2" />
                              Send Message
                            </>
                          )}
                        </Button>
                      </form>
                    </TabsContent>
                  ))}
                </Tabs>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Map Placeholder */}
      <section className="h-96 bg-slate-200 relative" data-testid="map-section">
        <div className="absolute inset-0 flex items-center justify-center">
          <div className="text-center">
            <MapPin className="w-12 h-12 text-slate-400 mx-auto mb-4" />
            <p className="text-slate-600">Staten Island, NY Office</p>
          </div>
        </div>
      </section>
    </div>
  );
}
